import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { MinimizedChatLayouts } from '@wix/inbox-common';
import styles from './ChatWidget.scss';
import { useCollapseExpand } from '../../hooks/collapse-expand';
import { useFirstExpandReporter } from '../../hooks/first-expand-reporter';
import { useInstanceRefresher } from '../../hooks/instance-refresher';
import {
  useColors,
  useDesign,
  useLayout,
  useTexts,
  useFonts,
} from '../../hooks/editor-settings';
import { useInitChat } from '../../hooks/chat-init';
import { useServices } from '../../hooks/services-registry';
import { AppState, useAppState } from '../../hooks/app-state';
import { AvailabilityStatus } from '../../types/availability-status';
import { DeviceType, ViewMode } from '../../types/host-sdk';
import { getThumbnail } from './thumbnail';
import { useQab } from '../../hooks/quick-action-button';
import { CommunicationChannelProvider, withSounds } from '@wix/chat-web';
import { useVisitorOnPageReporter } from '../../hooks/visitor-on-page';
import { useHasMessages } from '../../hooks/has-messages';
import { useVisibilityHandler } from '../../hooks/visibility-handler';
import { useNewMessageHandler } from '../../hooks/new-message-handler';
import { usePresence } from '../../hooks/presence';
import { useCorvidCommandApi } from '../../hooks/corvid-api';
import { fallbackFont, WINDOW_POSITION } from '../../constants';
import { useChatSettings } from '../../hooks/chat-settings';
import { useUpdateViewMode } from '../../hooks/update-view-mode';
import { useTranslation } from '@wix/wix-i18n-config';
import { usePresenceClient } from '../../hooks/presence-client';
import {
  SessionStorageKeys,
  useSessionStorage,
} from '../../hooks/useSessionStorage';

const ExpandedWidget = React.lazy(
  () =>
    import(
      /* webpackChunkName: "expanded-widget" */ '../ExpandedWidget/ExpandedWidget'
    ),
);

const SocialWidget = React.lazy(
  () =>
    import(
      /* webpackChunkName: "social-widget" */ '../Social/SocialWidget/SocialWidget'
    ),
);

const EditorWidget = React.lazy(
  () =>
    import(
      /* webpackChunkName: "editor-widget" */ '../EditorWidget/EditorWidget'
    ),
);

const MessagePopup = React.lazy(
  () =>
    import(
      /* webpackChunkName: "message-pop-up" */ '../MessagePopup/MessagePopup'
    ),
);

const MinimizedChat = React.lazy(
  () =>
    import(
      /* webpackChunkName: "minimized-widget" */ '../MinimizedChat/MinimizedChat'
    ),
);

export interface ChatWidgetProps {}

const _ChatWidget: React.FunctionComponent<ChatWidgetProps> = () => {
  const [isLoggedInMember, setIsLoggedInMember] = useState<boolean | undefined>(
    false,
  );

  const texts = useTexts();
  const design = useDesign();
  const layout = useLayout();
  const colors = useColors();
  const fonts = useFonts();
  const collapseExpand = useCollapseExpand();
  const { t } = useTranslation();

  const { hostSdk, fedopsLogger, experiments, biLogger } = useServices();
  const corvidCommandApi = useCorvidCommandApi();
  const { appState }: { appState: AppState } = useAppState();
  const {
    chatSettings: { socialChatEnabled },
  } = useChatSettings();
  const isMobile = appState.deviceType === DeviceType.Mobile;
  const isMembersChat = !!(socialChatEnabled && isLoggedInMember);

  const { isSdkLoaded } = useInitChat(experiments);
  const { getValue: getIsExpanded } = useSessionStorage<boolean>(
    SessionStorageKeys.IsExpanded,
  );
  const expandChatFromStorageValue = experiments.enabled(
    'specs.chat.expandChatFromStorageValue',
  );
  const viewMode = useMemo(() => hostSdk.getViewMode(), [hostSdk]);

  useUpdateViewMode();
  useInstanceRefresher();
  useQab();

  useEffect(() => {
    collapseExpand.collapse();
    hostSdk.reportApplicationLoaded();
    fedopsLogger.appLoaded();
    fedopsLogger.flush();
    biLogger.chatDisplayedEvent(
      appState.deviceType,
      design.minimizedChatLayout,
      appState.host,
    );
    corvidCommandApi.registerCorvidApi();
  }, []);

  useEffect(() => {
    void (async () => {
      const member = await hostSdk.getCurrentMember();
      const isPublicMember = member?.attributes?.privacyStatus === 'PUBLIC';
      const isBlockedMember = member?.status === 'DENIED';

      const _isLoggedInMember = isPublicMember && !isBlockedMember;
      setIsLoggedInMember(_isLoggedInMember);
    })();
  }, []);

  useEffect(() => {
    const expandedFromStorage = getIsExpanded();
    const shouldExpandIfStorageValueIsTrue =
      expandedFromStorage &&
      expandChatFromStorageValue &&
      viewMode === ViewMode.Site;

    if (!isMobile && shouldExpandIfStorageValueIsTrue) {
      void collapseExpand.expand();
    }
  }, [
    getIsExpanded,
    collapseExpand,
    isMobile,
    expandChatFromStorageValue,
    appState,
    viewMode,
  ]);

  useFirstExpandReporter();
  useVisitorOnPageReporter();
  useHasMessages();
  useVisibilityHandler();
  useNewMessageHandler(isSdkLoaded, isMembersChat);

  const isPresenceOverDuplexerSpecOn = experiments.enabled(
    'specs.PresenceOverDuplexerSpec',
  );

  const isBfCacheFixEnabled = experiments.enabled('specs.chat.BfCacheFix');

  const presenceClientHook = isPresenceOverDuplexerSpecOn
    ? usePresenceClient
    : usePresence;

  const { markAsActive } = presenceClientHook(isBfCacheFixEnabled);

  const thumbnail = getThumbnail(
    design,
    appState.businessInfo?.image,
    collapseExpand.isExpanded,
    isMembersChat,
    hostSdk.getResizedImgUrl,
  );
  const borderRadius = isMobile ? '24' : design.selectedRadiusOption;

  const isInEditingExperience =
    appState?.viewMode?.() === ViewMode.Editor ||
    appState?.viewMode?.() === ViewMode.Preview;

  const isOnline = appState.availabilityStatus === AvailabilityStatus.Available;
  const title = isMembersChat
    ? t('social.chatroom-list.title')
    : isOnline
    ? texts.onlineTitle
    : texts.offlineTitle;

  const isClassicEditor = appState?.editorType === 'wix';

  return (
    <div
      data-hook="chat-widget"
      ref={(elm) => elm && collapseExpand.setRef(elm)}
      className={classNames(styles.chatWidget, {
        [styles.left]: layout.position === WINDOW_POSITION.BOTTOM_LEFT,
        [styles.right]: layout.position === WINDOW_POSITION.BOTTOM_RIGHT,
        [styles.fixed]:
          design.minimizedChatLayout === MinimizedChatLayouts.Fixed,
        [styles.expanded]: collapseExpand.isExpanded,
        [styles.mobile]: isMobile,
        [styles.classicEditor]: isClassicEditor,
      })}
      onClick={markAsActive}
      role="main"
    >
      <CommunicationChannelProvider communicationChannels={{}}>
        {isSdkLoaded && !appState.withQab ? (
          <MessagePopup isMembersChat={isMembersChat} />
        ) : null}
        {isInEditingExperience && <EditorWidget />}
        {collapseExpand.isExpanded && isSdkLoaded ? (
          <>{isMembersChat ? <SocialWidget /> : <ExpandedWidget />}</>
        ) : null}
        {collapseExpand.isCollapsed &&
          !appState.withQab &&
          appState.isVisible &&
          (appState.editorSettingsLoaded ||
            appState?.viewMode?.() === ViewMode.Preview) && (
            <div
              data-hook="minimized-chat-wrapper"
              className={styles.minimizedChat}
            >
              <MinimizedChat
                layout={design.minimizedChatLayout}
                text={title}
                thumbnail={thumbnail}
                textColor={colors.minimizedChatTextColor.value}
                backgroundColor={colors.minimizedChatBackgroundColor.value}
                borderRadius={borderRadius}
                font={[fonts.header, fallbackFont].join(', ')}
                unreadCount={appState.unreadCount}
                onClick={() => collapseExpand.toggle('uou', 'widget')}
              />
            </div>
          )}
      </CommunicationChannelProvider>
    </div>
  );
};

_ChatWidget.displayName = 'ChatWidget';

export const ChatWidget = withSounds<ChatWidgetProps>({
  isChatActivePredicate: () => true,
})(_ChatWidget);
